import axios from "axios";
import { API_BASE_URL } from "../constants/apiConstants";
import { getDecryptedDataFromSessionStorage } from "../utils";
import { SECRET_KEY } from "../constants/encriptKey";

const TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";


export const getToken = async () => {
  const sessionData = getDecryptedDataFromSessionStorage(SECRET_KEY);
  return sessionData?.accessToken || null;
};

export const getRefreshToken = async () => {
  const sessionData = getDecryptedDataFromSessionStorage(SECRET_KEY);
  return sessionData?.refreshToken || null;
};

export function deleteToken() {
  localStorage.clear();
  sessionStorage.clear();
}

export const setToken = (token) => {
  const sessionData = JSON.parse(sessionStorage.getItem("sessionData")) || {};
  sessionData.accessToken = token;
  sessionStorage.setItem("sessionData", JSON.stringify(sessionData));
};

export const setRefreshToken = (refreshToken) => {
  const sessionData = JSON.parse(sessionStorage.getItem("sessionData")) || {};
  sessionData.refreshToken = refreshToken;
  sessionStorage.setItem("sessionData", JSON.stringify(sessionData));
};

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Error fetching token:", error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const urlRefresh = `${API_BASE_URL}backoffice/LoginBackoffice/refresh-token`;

    if (error.response?.status === 429) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      if (originalRequest._retryCount <= 3) {
        const retryAfter = error.response.headers["retry-after"];
        const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
        return axiosInstance(originalRequest);
      }
      const base = process.env.PUBLIC_URL || "";
      sessionStorage.setItem("errorReturnPath", window.location.pathname);
      window.location.replace(`${base}/error?reason=429`);
    }

    if (error.response?.status === 401 && originalRequest.url === urlRefresh) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refresh = {
          refreshToken: await getRefreshToken(),
        };

        const res = await axios.post(urlRefresh, refresh);
        setToken(res.data.result.accessToken);
        setRefreshToken(res.data.result.refreshToken);

        axiosInstance.defaults.headers.common.Authorization =
          `Bearer ${res.data.result.accessToken}`;
        originalRequest.headers.Authorization =
          `Bearer ${res.data.result.accessToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError.response || refreshError.message);
        deleteToken();
        window.location.replace("/backoffice");
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 401) {
      deleteToken();
      window.location.replace("/backoffice");
      return Promise.reject(error);
    }

    if (error.response?.status === 403) {
      const base = process.env.PUBLIC_URL || "";
      window.location.replace(`${base}/error?reason=403`);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
